/**
 * @file TranslationProcessor.ts
 *
 * @description
 * REST-client wrapper around the Azure Cognitive Services Text Translation API
 * (`@azure-rest/ai-translation-text`). It provides a queue-and-poll mechanism
 * for translating recognized speech text into one or more target languages and
 * delivering the results via a callback.
 *
 * ## Role in the pipeline
 * Intended position: SpeechProcessor (recognition) → TranslationProcessor
 * (translation) → Text2SpeechProcessor (TTS) → AudioPlayer (playback).
 * Callers enqueue finalized recognized-text strings via `enqueueText()`; the
 * processor translates them in the background and fires `onTranslationReady`
 * with a `Map<Language, TranslationResult>` for each completed translation.
 *
 * ## Queue-and-poll design
 * `start()` establishes a `setInterval` that fires every **1 000 ms**. Each
 * tick dequeues at most **one** string and issues a single HTTP POST to
 * `/translate`. High-throughput scenarios will therefore introduce latency
 * proportional to queue depth (one item per second ceiling). There is no
 * batching, priority, or back-pressure mechanism.
 *
 * ## REST client vs SDK-based translation
 * This class uses the `@azure-rest/ai-translation-text` REST client and
 * authenticates with a bare **API key + region** pair stored in the instance
 * (`apiKey` / `region = "eastus"`). This is fundamentally different from the
 * translation path used in production: `SessionContext` and
 * `HybridModelSessionContext` rely on the Azure Speech SDK's
 * `TranslationRecognizer`, which performs recognition and translation in a
 * single streaming WebSocket round-trip and authenticates via a short-lived
 * authorization token (`SpeechConfig.fromAuthorizationToken()`). The REST
 * approach here requires an additional HTTP request per utterance and exposes
 * a long-lived API key rather than a ephemeral token.
 *
 * ## Usage status — LEGACY / DEAD CODE
 * As of the current codebase, this class is **never instantiated at runtime**.
 * `SessionContext` defines a private factory method `startTranslationProcessor`
 * that wraps the constructor, but that factory method is itself never called
 * from `start()`, `setupRecognition()`, or any other execution path. No other
 * file outside of `SessionContext.ts` imports `TranslationProcessor`. The class
 * is imported but effectively dead code, superseded by the `TranslationRecognizer`
 * SDK path used by both session context implementations.
 *
 * ## Known limitations
 * - No retry logic: if the HTTP POST fails or `isUnexpected()` returns true,
 *   the error is thrown inside an async IIFE but not caught; the text is
 *   silently lost and no recovery is attempted.
 * - Single item per poll cycle: queue backlog grows unbounded under load.
 * - Hard-coded region (`"eastus"`): not configurable without modifying the class.
 * - API key authentication: exposes a long-lived credential in frontend memory
 *   rather than the token-based pattern required by the project security policy.
 */
import {Queue} from "./Queue";
import TextTranslationClient, {isUnexpected} from "@azure-rest/ai-translation-text";
import {
    Language,
    RecognizableAndTranslatableLanguage,
    TranslatableAndSpeakableLanguage,
    TranslationResult
} from "./Configuration";

export class TranslationProcessor {

    private endpoint = 'https://api.cognitive.microsofttranslator.com';
    private apiKey;
    private region = "eastus";

    private pendingTranslationQueue: Queue<string>;
    private inputLanguage: RecognizableAndTranslatableLanguage;
    private outputLanguages: TranslatableAndSpeakableLanguage[];
    private outputLanguageIds: string;
    private onTranslationReady: (translationsReady: Map<Language, TranslationResult>) => void;

    private pollingInterval: NodeJS.Timer | undefined = undefined;

    private translationClient;

    constructor(translationKey: string, inputLanguage: RecognizableAndTranslatableLanguage,
                outputLanguages: TranslatableAndSpeakableLanguage[],
                onTranslationReady: (translationsReady: Map<Language, TranslationResult>) => void) {
        this.apiKey = translationKey;
        this.translationClient = TextTranslationClient(this.endpoint, {
            key: this.apiKey,
            region: this.region
        })

        this.pendingTranslationQueue = new Queue<string>();
        this.inputLanguage = inputLanguage;
        this.outputLanguages = outputLanguages;
        this.outputLanguageIds = outputLanguages.map(output => output.translationKey).join(",");
        this.onTranslationReady = onTranslationReady;
    }

    public start = (): void => {

        if (this.pollingInterval) {
            throw new Error("Cant start TranslationProcess");
        }

        this.pollingInterval = setInterval(() => {

            let textToTranslate = this.pendingTranslationQueue.dequeue();
            if (!!textToTranslate)
                //console.log("will translate", textToTranslate)
                this.translate(textToTranslate);

        }, 1000)
    }

    public stop = (): void => {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval)
            console.log("[Session Context] [4] Stopped Translation ")
        }
    }

    public enqueueText = (textToTranslate: string): void => {
        this.pendingTranslationQueue.enqueue(textToTranslate);
    }

    private translate = (text: string) => {

        console.log('[Translation Processor] Will translate...', text);
        this.translationClient.path("/translate")
            .post({
                body: [{text: text}],
                queryParameters: {
                    from: this.inputLanguage.recognitionKey.split("-")[0],//"en",
                    to: this.outputLanguageIds,//"es",
                }
            })
            .then(translateResponse => {

                const solveTranslationResponse = async () => {
                    if (isUnexpected(translateResponse)) {
                        throw translateResponse.body.error;
                    }

                    const translationResponseBody = translateResponse.body;
                    // console.log("SENT raw 0", translationResponseBody)
                    for (const translation of translationResponseBody) {
                        translation.translations.forEach(tr => {
                            // console.log("SENT raw 1", tr, translation)
                            console.log(
                                `Text was translated to: '${tr.to}' and the result is: '${tr.text}'.`
                            );
                        })

                        let parsedTranslations = new Map<Language, TranslationResult>();
                        translation.translations.forEach(tr => {
                            const lang = {id: tr.to} as Language;
                            // console.log("SENT raw 2", tr)
                            const translationResult = {
                                originalText: translation.sourceText || text || '',
                                translatedText: tr.text,
                                status: '',
                                language: lang
                            } as TranslationResult;
                            parsedTranslations.set(lang, translationResult);
                        });

                        this.onTranslationReady(parsedTranslations);
                    }
                };

                solveTranslationResponse();
            });
    }
}
